<template>
  <div v-if="pageData" class="w-full flex flex-col transition-colors duration-700 ease-in-out">
    <!-- Navbar -->
    <NavBar ref="bar"
            :company-name="pageData.companyName || pageData.realCompanyName"
            :logo="pageData.companyLogo"
            :nav-setup="pageData?.navSetup"
            :available-pages="pageData?.availablePages"
            :class="navClass"
            :show-company-name="pageData.showCompanyName"
            start-dimmed
            :product-page-name="pageData.productPageName"
            :showcase-page-name="pageData.showcasePageName"
    />

    <!-- Blog Posts Section -->
    <div class="max-w-3xl w-full mx-auto px-4"
         :style="{ marginTop: `${navHeight}px`} "
    >

      <!-- The page's one H1 (each post card below has its own H2). Hidden: the page has never shown a title. -->
      <h1 class="sr-only">Posts</h1>

      <!-- Column-Based Layout -->
      <div class="flex flex-col space-y-10 w-full">
        <div v-for="(post, index) in posts"
             :key="post.id"
             ref="postElements"
             :class="[
       isHydrated ? 'opacity-0 transform translate-y-10 transition-all duration-700 ease-out ease-in' : '',
              ]">
          <transition name="fade">
            <PostCard
                :post="post"
                :colors="colorStore.colors"
                :background-variant="index % 2 === 0 ? 1 : 2"/>
          </transition>


          <!-- Animated Mobile Divider -->
          <div v-if="isMobile"
               ref="dividerElements"
               :class="[
                isHydrated ? 'opacity-0 transform translate-y-10 transition-all duration-700 ease-out h-8 w-full flex items-center justify-center' : 'h-8 w-full flex items-center justify-center'
              ]"          >
            <div class="bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 w-3/4 h-0.5 rounded-full"></div>
          </div>
        </div>

        <!-- Skeleton Loaders -->
        <transition name="fade">
        <div v-if="loading">
          <SkeletonCard  v-for="n in 3" :key="'skeleton-' + n" />
        </div>
        </transition>
      </div>

      <!-- Infinite Scroll Trigger -->
      <div ref="scrollTrigger" class="h-10"></div>

      <!-- Load More Button (Fallback) -->
      <button v-if="hasMorePosts" @click="fetchPosts" class="mt-6 mx-auto block bg-primary text-white px-6 py-2 rounded-lg hover:opacity-80 transition">
        Load More
      </button>
    </div>

    <!-- Footer -->
    <FooterSection
        :companyName="pageData.companyName || pageData.realCompanyName"
        :companyTagline="pageData.companyTagline"
        :registrationInfo="pageData.registrationInfo"
        :colors="pageData.brandColors"
        :footerLinks="[
        { label: 'Home', url: '/' },
        { label: 'Services', url: '/services' },
        { label: 'Support', url: '/support' },
        { label: 'Terms', url: '/terms' }
      ]"
        :socialLinks="pageData?.socialLinks ?? []"
        :show-credits="false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from "vue";
import NavBar from "@/components/NavBar.vue";
import FooterSection from "@/components/FooterSection.vue";
import PostCard from "@/components/PostCard.vue";
import SkeletonCard from "@/components/SkeletonCard.vue";
import { fetchMockPosts } from "@/classes/mockPosts.js";
import { fetchBrandConfig } from "@/classes/mockBrand.js";
import { useRoute } from "vue-router";
import { usePageStore } from "@/stores/pageStore";
import { useColorStore } from "@/classes/useColorStore.js";

const pageStore = usePageStore();
const colorStore = useColorStore();
const navHeight = ref(0);
const bar = ref(null);
const isHydrated = ref(false);
const route = useRoute();

const pageData = computed(() => pageStore.pageData); // ✅ Access injected data
const posts = ref([]);
const brandConfig = ref({});
const loading = ref(false);
const page = ref(1);
const hasMorePosts = ref(true);
const scrollTrigger = ref(null);
const postElements = ref([]);
const dividerElements = ref([]);
let observer = null;

// Detect Mobile View (SSR Safe)
const isMobile = ref(false);

// Fetch Posts with Pagination
const fetchPosts = async () => {
  if (loading.value || !hasMorePosts.value) return;

  loading.value = true;

  let prefix = '';
  if (import.meta.env.VITE_IS_PREVIEW_SERVER === 'true'){
    prefix = '/api/preview'
  }

  const response = await fetch(`${prefix}/api/page-data/posts?page=${page.value}`);
  const newPosts = await response.json();
  console.log("Checking posts", newPosts, response);
  if (newPosts !== undefined && newPosts?.length > 0) {
    console.log('Adding posts', newPosts, posts);
    posts.value.push(...newPosts);
    console.log('Added posts', posts);
    page.value++;
  } else {
    hasMorePosts.value = false;
  }

  loading.value = false;
  nextTick(() => {
    observeScroll(); // Reattach observer after loading new posts
    animatePosts(); // Animate new posts
  });
};

// Fetch Page Data & Brand Config
const loadPageData = async () => {
  brandConfig.value = await fetchBrandConfig();
};

// Infinite Scroll Observer
const observeScroll = () => {
  if (observer) observer.disconnect();

  observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) fetchPosts();
      },
      { rootMargin: "100px" }
  );

  if (scrollTrigger.value) observer.observe(scrollTrigger.value);
};

// Animate Posts & Dividers as They Scroll Into View
const animatePosts = () => {
  const revealOnScroll = (elements) => {
    elements.forEach((el, index) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.99) {
        setTimeout(() => {
          el.classList.remove("opacity-0", "translate-y-10");
        }, index * 100); // Sequential animation for better effect
      }
    });
  };

  nextTick(() => {
    revealOnScroll(postElements.value);
    revealOnScroll(dividerElements.value);
  });
};

// Listen to Scroll Event for Animations
const handleScroll = () => {
  animatePosts();
};

onMounted(async () => {
  isHydrated.value = true;

  // Prevent SSR execution of window-related operations
  if (!import.meta.env.SSR) {
    isMobile.value = window.innerWidth < 768;
    window.addEventListener("resize", () => {
      isMobile.value = window.innerWidth < 768;
    });
  }
  else {

  }

  await loadPageData();
  await fetchPosts();
  observeScroll();
  animatePosts();

  window.addEventListener("scroll", handleScroll);

  nextTick(() => {
    if (bar.value && typeof bar.value.barHeight === "function") {
      navHeight.value = bar.value.barHeight();
    }
  });
});

onUnmounted(() => {
  if (observer) observer.disconnect();
  window.removeEventListener("scroll", handleScroll);
  if (!import.meta.env.SSR) {
    window.removeEventListener("resize", () => {
      isMobile.value = window.innerWidth < 768;
    });
  }
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.8s ease;
}
.fade-enter-from, .fade-leave-to {
  transition: opacity 0.8s ease;
  opacity: 0;
}
</style>
